import { Home, Store } from "lucide-react";

export const SECTORS = {
  residenziale: {
    id: "residenziale",
    label: "Residenziale",
    navLabel: "Casa",
    title: "Energia Solare per la Tua Casa",
    subtitle: "Riduci la bolletta fino al 90% con un impianto fotovoltaico su misura per la tua abitazione. Preventivi gratuiti da installatori certificati.",
    icon: Home,
    color: "emerald",
    heroImage: "https://images.unsplash.com/photo-1508514177221-188b1cf16e9d?w=1200&q=80",
    stats: [
      { value: "90%", label: "Risparmio bolletta" },
      { value: "25", label: "Anni di garanzia" },
      { value: "5-7", label: "Anni di rientro" },
      { value: "50%", label: "Detrazione fiscale" },
    ],
    benefits: [
      { title: "Risparmio Immediato", desc: "Riduci la bolletta elettrica dal primo giorno di attivazione dell'impianto." },
      { title: "Valore Immobile", desc: "Un impianto fotovoltaico aumenta il valore della tua casa fino al 10%." },
      { title: "Indipendenza Energetica", desc: "Produci la tua energia e riduci la dipendenza dalla rete elettrica." },
      { title: "Incentivi Fiscali", desc: "Detrazione del 50% recuperabile in 10 anni sulla dichiarazione dei redditi." },
    ],
    calculator: {
      title: "Calcola il Tuo Risparmio",
      fields: [
        { key: "monthly_consumption", label: "Consumo mensile (kWh)", type: "range", min: 100, max: 800, step: 50, default: 300, unit: "kWh" },
        { key: "roof_type", label: "Tipo di tetto", type: "select", options: ["Tegole", "Coppi", "Lamiera", "Piano"], default: "Tegole" },
      ],
      calculate: (values) => {
        const annual = values.monthly_consumption * 12;
        const kWp = Math.ceil(annual / 1300);
        const savings = Math.round(kWp * 1300 * 0.25);
        const co2 = ((kWp * 1300 * 0.4) / 1000).toFixed(1);
        return [
          { label: "Potenza consigliata", value: `${kWp} kWp` },
          { label: "Risparmio annuale", value: `${savings.toLocaleString("it-IT")} €` },
          { label: "CO₂ risparmiata/anno", value: `${co2} ton` },
          { label: "Rientro investimento", value: `${kWp <= 3 ? "5-6" : "6-8"} anni` },
        ];
      },
    },
    formFields: [
      { key: "name", label: "Nome", type: "text", required: true, half: true },
      { key: "surname", label: "Cognome", type: "text", half: true },
      { key: "email", label: "Email", type: "email", required: true },
      { key: "phone", label: "Telefono", type: "tel", required: true, otp: true },
      { key: "property_type", label: "Tipo di abitazione", type: "select", options: ["Casa indipendente", "Appartamento", "Villa", "Bifamiliare"], half: true },
      { key: "roof_type", label: "Tipo di tetto", type: "select", options: ["Tegole", "Coppi", "Lamiera", "Piano"], half: true },
      { key: "monthly_consumption", label: "Consumo mensile (kWh)", type: "number" },
    ],
    faq: [
      { q: "Quanto costa un impianto fotovoltaico per casa?", a: "Il costo medio per un impianto da 3-6 kWp varia tra 5.000 e 12.000 euro, prima delle detrazioni fiscali del 50%." },
      { q: "Quanto tempo serve per installare un impianto?", a: "L'installazione richiede generalmente 2-3 giorni lavorativi. Le pratiche burocratiche possono richiedere 2-4 settimane." },
      { q: "Conviene aggiungere una batteria di accumulo?", a: "Se consumi molta energia la sera, la batteria ti permette di aumentare l'autoconsumo dall'40% all'80%. Valuta con il nostro calcolatore." },
      { q: "Quanto dura un impianto fotovoltaico?", a: "I pannelli moderni hanno una garanzia di 25 anni e una vita utile di oltre 30 anni, con una perdita di efficienza minima." },
    ],
  },
  pmi: {
    id: "pmi",
    label: "PMI",
    navLabel: "PMI",
    title: "Fotovoltaico per PMI e Commercio",
    subtitle: "Soluzioni su misura per piccole e medie imprese, negozi e studi professionali. Riduci i costi fissi e aumenta la competitivita.",
    icon: Store,
    color: "violet",
    heroImage: "https://images.unsplash.com/photo-1560179707-f14e90ef3623?w=1200&q=80",
    stats: [
      { value: "60%", label: "Risparmio energia" },
      { value: "4-6", label: "Anni di rientro" },
      { value: "50%", label: "Detrazione fiscale" },
      { value: "100%", label: "Chiavi in mano" },
    ],
    benefits: [
      { title: "Costi Fissi Ridotti", desc: "Riduci drasticamente le spese energetiche della tua attivita commerciale." },
      { title: "Competitivita", desc: "Margini piu alti grazie ai minori costi operativi rispetto ai concorrenti." },
      { title: "Incentivi PMI", desc: "Detrazione 50%, ammortamento e incentivi regionali specifici per le PMI." },
      { title: "Servizio Completo", desc: "Dall'analisi alla manutenzione, tutto gestito dai nostri partner certificati." },
    ],
    calculator: {
      title: "Calcola il Risparmio per la Tua Attivita",
      fields: [
        { key: "monthly_consumption", label: "Consumo mensile (kWh)", type: "range", min: 200, max: 5000, step: 100, default: 1000, unit: "kWh" },
        { key: "business_type", label: "Tipo di attivita", type: "select", options: ["Negozio", "Ristorante", "Studio professionale", "Laboratorio", "Ufficio", "Altro"], default: "Negozio" },
      ],
      calculate: (values) => {
        const annual = values.monthly_consumption * 12;
        const kWp = Math.ceil(annual / 1250);
        const savings = Math.round(kWp * 1250 * 0.23);
        const co2 = ((kWp * 1250 * 0.4) / 1000).toFixed(1);
        return [
          { label: "Potenza consigliata", value: `${kWp} kWp` },
          { label: "Risparmio annuale", value: `${savings.toLocaleString("it-IT")} €` },
          { label: "CO₂ risparmiata/anno", value: `${co2} ton` },
          { label: "Rientro investimento", value: `${kWp <= 10 ? "4-5" : "5-7"} anni` },
        ];
      },
    },
    formFields: [
      { key: "company_name", label: "Nome Attivita", type: "text", required: true },
      { key: "name", label: "Referente", type: "text", required: true, half: true },
      { key: "surname", label: "Cognome", type: "text", half: true },
      { key: "email", label: "Email", type: "email", required: true },
      { key: "phone", label: "Telefono", type: "tel", required: true, otp: true },
      { key: "business_type", label: "Tipo di attivita", type: "select", options: ["Negozio", "Ristorante", "Studio professionale", "Laboratorio", "Ufficio", "Altro"], half: true },
      { key: "monthly_consumption", label: "Consumo mensile (kWh)", type: "number", half: true },
      { key: "num_employees", label: "Numero dipendenti", type: "number" },
    ],
    faq: [
      { q: "Quanto costa un impianto per un negozio?", a: "Per un negozio medio (consumo 500-1500 kWh/mese), un impianto da 5-15 kWp costa tra 8.000 e 20.000 euro prima degli incentivi." },
      { q: "Posso installare i pannelli su un tetto in affitto?", a: "Si, con l'autorizzazione del proprietario. In alternativa, esistono soluzioni di noleggio operativo che non richiedono investimento iniziale." },
      { q: "Quanto tempo richiede l'installazione?", a: "Per una PMI, l'installazione richiede 3-5 giorni lavorativi. Le pratiche burocratiche 3-6 settimane." },
      { q: "E possibile finanziare l'impianto?", a: "Si, offriamo soluzioni di finanziamento a tasso agevolato, leasing e noleggio operativo." },
    ],
  },
};

export const SECTOR_LIST = Object.values(SECTORS);

export const SECTOR_COLORS = {
  emerald: { bg: "bg-emerald-500", light: "bg-emerald-50", text: "text-emerald-700", border: "border-emerald-200", gradient: "from-emerald-600 to-emerald-800" },
  blue: { bg: "bg-blue-500", light: "bg-blue-50", text: "text-blue-700", border: "border-blue-200", gradient: "from-blue-600 to-blue-800" },
  amber: { bg: "bg-amber-500", light: "bg-amber-50", text: "text-amber-700", border: "border-amber-200", gradient: "from-amber-600 to-amber-800" },
  violet: { bg: "bg-violet-500", light: "bg-violet-50", text: "text-violet-700", border: "border-violet-200", gradient: "from-violet-600 to-violet-800" },
};
